import React from "react";
import { View, StyleSheet } from "react-native";
import CustomText from "../../components/common/general/CustomText/CustomText";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";
import CustomTextInput from "../../components/common/general/CustomTextInput/CustomTextInput";
import CustomButton from "../../components/common/general/CustomButton/CustomButton";
import Divider from "../../components/common/layout/Divider/Divider";
import { Checked, UnChecked } from "../../components/svgs/common";

interface FieldSchema {
  type: "string" | "integer" | "boolean" | "array";
  [key: string]: any;
}

interface SchemaDefinition {
  properties: Record<string, FieldSchema>;
}

interface DynamicFormContainerProps {
  schema: SchemaDefinition;
  originalValues?: Record<string, any>;
  values: Record<string, any>;
  onValueChange: (key: string, value: any) => void;
  onSavePressed?: () => void;
  showSaveButton?: boolean;
  saveButtonText?: string;
  saveButtonStyleType?: "primary" | "lightGreen" | "danger" | "transparent";
}

const deepEqual = (a: any, b: any): boolean => {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (a && b && typeof a === "object") {
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      return a.every((v, i) => deepEqual(v, b[i]));
    }
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    return keysA.every((key) => deepEqual(a[key], b[key]));
  }
  return false;
};

const DynamicFormContainer: React.FC<DynamicFormContainerProps> = ({
  schema,
  originalValues,
  values,
  onValueChange,
  onSavePressed,
  showSaveButton = true,
  saveButtonText = "Save Changes",
  saveButtonStyleType = "lightGreen",
}) => {
  const renderField = (key: string, field: FieldSchema) => {
    const isDiff = !!originalValues && !deepEqual(originalValues[key], values[key]);
    const isRequired = key === "stripeSecretKey" || key === "tenantName" || key === "tenantDomain";
    const labelStyle = isDiff ? { color: "#d9534f" } : isRequired ? { color: "#d9534f" } : {};
    const labelWeight = isDiff ? "ultraBold" : isRequired ? "ultraBold" : "medium";

    switch (field.type) {
      case "integer":
        return (
          <View key={key} style={styles.fieldContainer}>
            <CustomText
              role="header"
              text={key}
              size="m"
              weight={labelWeight}
              style={labelStyle}
              useTranslationText={false}
            />
            <CustomSpacing size="s" />
            <CustomTextInput
              value={values[key] != null ? String(values[key]) : ""}
              onChangeText={(text) => {
                const parsed = parseInt(text, 10);
                onValueChange(key, !isNaN(parsed) ? parsed : "");
              }}
              keyboardType="numeric"
              placeholder={`Enter ${key}`}
              style={{}}
            />
            <Divider size="l" />
          </View>
        );

      case "string":
        return (
          <View key={key} style={styles.fieldContainer}>
            <CustomText
              role="header"
              text={isRequired ? `${key} *` : key}
              size="m"
              weight={labelWeight}
              style={labelStyle}
              useTranslationText={false}
            />
            <CustomSpacing size="s" />
            <CustomTextInput
              value={values[key] || ""}
              onChangeText={(text) => onValueChange(key, text)}
              multiline
              placeholder={isRequired ? `Enter ${key} (required)` : `Enter ${key}`}
              style={{}}
            />
            <Divider size="l" />
          </View>
        );

      case "boolean":
        return (
          <View key={key} style={styles.fieldContainer}>
            <View style={styles.booleanRow}>
              <CustomText
                role="header"
                text={key}
                size="m"
                weight={labelWeight}
                style={labelStyle}
                useTranslationText={false}
              />
              <CustomButton
                styleType="transparent"
                leftIcon={values[key] ? <Checked /> : <UnChecked />}
                onPress={() => onValueChange(key, !values[key])}
              />
            </View>
            <Divider size="l" />
          </View>
        );

      case "array":
        return (
          <View key={key} style={styles.fieldContainer}>
            <CustomText
              role="header"
              text={key}
              size="m"
              weight={labelWeight}
              style={labelStyle}
              useTranslationText={false}
            />
            <CustomSpacing size="s" />
            {Array.isArray(values[key]) &&
              values[key].map((item: any, idx: number) => (
                <View key={idx} style={styles.arrayItemRow}>
                  <CustomTextInput
                    value={String(item)}
                    onChangeText={(text) => {
                      const arr = [...values[key]];
                      arr[idx] = text;
                      onValueChange(key, arr);
                    }}
                    placeholder="Array item"
                    style={{}}
                  />
                  <CustomButton
                    text="Remove"
                    styleType="danger"
                    size="s"
                    onPress={() => {
                      const arr = [...values[key]];
                      arr.splice(idx, 1);
                      onValueChange(key, arr);
                    }}
                  />
                </View>
              ))}
            <CustomButton
              text="Add"
              size="s"
              styleType="primary"
              onPress={() => onValueChange(key, [...(values[key] || []), ""])}
            />
            <Divider size="l" />
          </View>
        );

      default:
        return null;
    }
  };

  return (
    <>
      {Object.entries(schema.properties).map(([key, field]) => renderField(key, field))}
      {showSaveButton && onSavePressed && (
        <>
          <CustomSpacing size="l" />
          <CustomButton text={saveButtonText} styleType={saveButtonStyleType} onPress={onSavePressed} />
        </>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  fieldContainer: {
    marginBottom: 16,
  },
  booleanRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginVertical: 8,
  },
  arrayItemRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
});

export default DynamicFormContainer;
